/**
 * Timeline building utilities for printable cooking plan
 * Handles lane creation, step merging, conflict detection, and scaling
 */

/**
 * Compute scale (seconds per pixel) and tick interval
 * @param {number} makespanSec - Total duration in seconds
 * @param {number} targetWidthPx - Target width in pixels (default: 960)
 * @returns {Object} { sPerPx: number, tickEvery: number (seconds) }
 */
export const computeScale = (makespanSec, targetWidthPx = 1200) => {
    if (!makespanSec || makespanSec === 0) {
        return { sPerPx: 6, tickEvery: 300 };
    }
    
    // Calculate seconds per pixel - use even smaller scale to extend timeline
    // This makes the timeline wider so all content is visible
    // Clamp to minimum 2s/px for maximum timeline extension
    const sPerPx = Math.max(2, Math.ceil(makespanSec / targetWidthPx));
    
    // Determine tick interval based on makespan
    let tickEvery;
    if (makespanSec > 3 * 3600) {
        tickEvery = 900; // 15 minutes
    } else if (makespanSec > 2 * 3600) {
        tickEvery = 600; // 10 minutes
    } else {
        tickEvery = 300; // 5 minutes
    }
    
    return { sPerPx, tickEvery };
};

/**
 * Format time as HH:MM:SS or MM:SS
 * @param {number} sec - Time in seconds
 * @returns {string} Formatted time string
 */
export const fmt = (sec) => {
    if (sec >= 3600) {
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        const s = sec % 60;
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/**
 * Build timeline lanes with merged contiguous segments
 * @param {Array} segments - Array of segment objects
 * @returns {Object} { lanes: Object<recipeName, segments[]>, conflicts: Array }
 */
export const buildTimeline = (segments) => {
    // Sort segments by start time
    const sorted = [...segments].sort((a, b) => a.startSec - b.startSec);
    
    // Build lanes and merge contiguous segments
    const lanes = {};
    
    for (const s of sorted) {
        const recipeName = s.recipeName;
        
        if (!lanes[recipeName]) {
            lanes[recipeName] = [];
        }
        
        const arr = lanes[recipeName];
        const last = arr[arr.length - 1];
        
        // Merge if: same type, same step title, and contiguous (end === start)
        if (last && 
            last.type === s.type && 
            last.stepTitle === s.stepTitle && 
            Math.abs(last.endSec - s.startSec) < 1) { // Allow 1s tolerance
            // Merge: extend end time and add duration
            last.endSec = s.endSec;
            last.durationSec = last.endSec - last.startSec;
        } else {
            // New segment
            arr.push({ ...s });
        }
    }
    
    // Detect conflicts (overlapping active segments across recipes)
    const conflicts = detectConflicts(sorted);
    
    return { lanes, conflicts, allSegments: sorted };
};

/**
 * Detect conflicts where active segments overlap across different recipes
 * @param {Array} segments - All segments sorted by start time
 * @returns {Array} Array of conflict objects { startSec, endSec, recipes: [] }
 */
const detectConflicts = (segments) => {
    const conflicts = [];
    const activeSegments = segments.filter(s => s.type === 'active');
    
    // Check each pair of active segments
    for (let i = 0; i < activeSegments.length; i++) {
        for (let j = i + 1; j < activeSegments.length; j++) {
            const seg1 = activeSegments[i];
            const seg2 = activeSegments[j];
            
            // Skip if same recipe
            if (seg1.recipeId === seg2.recipeId) continue;
            
            // Check for overlap
            const overlapStart = Math.max(seg1.startSec, seg2.startSec);
            const overlapEnd = Math.min(seg1.endSec, seg2.endSec);
            
            if (overlapStart < overlapEnd) {
                // Found a conflict
                conflicts.push({
                    startSec: overlapStart,
                    endSec: overlapEnd,
                    durationSec: overlapEnd - overlapStart,
                    recipes: [
                        { name: seg1.recipeName, step: seg1.stepTitle },
                        { name: seg2.recipeName, step: seg2.stepTitle }
                    ]
                });
            }
        }
    }
    
    // Merge overlapping conflicts
    return mergeOverlappingConflicts(conflicts);
};

/**
 * Merge conflicts that overlap in time
 * @param {Array} conflicts - Array of conflict objects
 * @returns {Array} Merged conflicts
 */
const mergeOverlappingConflicts = (conflicts) => {
    if (conflicts.length === 0) return [];
    
    // Sort by start time
    const sorted = [...conflicts].sort((a, b) => a.startSec - b.startSec);
    const merged = [];
    let current = { ...sorted[0] };
    
    for (let i = 1; i < sorted.length; i++) {
        const next = sorted[i];
        
        // Check if conflicts overlap
        if (current.endSec >= next.startSec) {
            // Merge: extend end time and combine recipes
            current.endSec = Math.max(current.endSec, next.endSec);
            current.durationSec = current.endSec - current.startSec;
            
            // Combine unique recipes
            const recipeNames = new Set([
                ...current.recipes.map(r => r.name),
                ...next.recipes.map(r => r.name)
            ]);
            current.recipes = Array.from(recipeNames).map(name => {
                const recipe = [...current.recipes, ...next.recipes].find(r => r.name === name);
                return recipe || { name, step: 'Unknown' };
            });
        } else {
            // No overlap, push current and start new
            merged.push(current);
            current = { ...next };
        }
    }
    
    merged.push(current);
    return merged;
};

/**
 * Generate time ruler ticks
 * @param {number} makespanSec - Total duration in seconds
 * @param {number} tickEvery - Tick interval in seconds
 * @returns {Array} Array of { time: number (seconds), label: string }
 */
export const generateTimeTicks = (makespanSec, tickEvery) => {
    const ticks = [];
    
    for (let time = 0; time <= makespanSec; time += tickEvery) {
        ticks.push({
            time,
            label: fmt(time)
        });
    }
    
    return ticks;
};
